import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MindARThree } from "mind-ar/dist/mindar-image-three.prod.js";
import { createVolcan3Effects } from "./volcan3Effects";

/**
 * =============================================================================
 * CONFIGURACIÓN DE MODELOS AR (MindAR)
 * =============================================================================
 *--------
 * Cada entrada corresponde a UN índice de imagen en tu archivo .mind
 * (orden: imagen 0 → entrada [0], imagen 1 → [1], …). Debe coincidir con el
 * orden usado al compilar targetsx3.mind (o el .mind que uses).
 *
 * Cómo editar:
 * - path: ruta bajo /public (ej. "/models/foo.glb").
 * - scale: escala base del modelo (1 = tamaño del archivo; valores <1 más pequeño).
 * - animation: controla el mixer; por defecto se reproducen todos los clips a la vez.
 *     - enabled: false → no se reproduce animación (modelo estático).
 *     - clipName / clipNames: si se definen, solo esos clips (nombres exactos del GLB).
 *     - timeScale: velocidad aplicada a cada clip (1 = normal, 0.5 = mitad, 2 = doble).
 *     - loop: true = LoopRepeat en cada clip (típico en RA).
 * - pin: UI del pin flotante sobre la escena.
 *     - show: false → no se muestra el pin al detectar ESTE modelo.
 *     - text: texto del globo (se inserta en el <p> del pin en ARPage).
 * - pinchMinScale / pinchMaxScale: límites relativos a la escala base (0.3 = 30%, 3 = 300%).
 * - idleRotationSpeed: giro automático en Y cuando no arrastras (0 = sin giro extra).
 * - position: traslación opcional { x, y, z } en el espacio del ancla, después de centrar
 *   el modelo con su bounding box (mismas unidades que la escena MindAR / Three.js).
 * - rotation: rotación inicial opcional { x, y, z } en radianes (eje local del modelo,
 *   después de centrar y antes del pinch). La Y debe coincidir con la base usada al arrastrar.
 *
 * Tras cambiar paths o número de modelos, asegúrate de que el .mind tenga la
 * misma cantidad de imágenes objetivo y en el mismo orden.
 * =============================================================================
 */

/**
 * @typedef {object} ARModelPinConfig
 * @property {boolean} [show=true] Si false, no se muestra el pin al detectar este target.
 * @property {string} [text] Texto del mensaje del pin (se asigna al <p> en la UI).
 */

/**
 * @typedef {object} ARModelAnimationConfig
 * @property {boolean} [enabled=true] false = modelo sin reproducir clips.
 * @property {string} [clipName] Un solo clip por nombre (alternativa a clipNames).
 * @property {string[]} [clipNames] Solo estos clips; si se omite, se reproducen todos.
 * @property {number} [timeScale=1] Velocidad de reproducción (todos los clips).
 * @property {boolean} [loop=true] Repetir cada clip.
 */

/**
 * @typedef {object} ARModelConfig
 * @property {string} id Identificador corto (logs / depuración).
 * @property {string} path Ruta del .glb bajo public/.
 * @property {number} scale Escala base uniforme.
 * @property {ARModelAnimationConfig} [animation]
 * @property {ARModelPinConfig} [pin]
 * @property {number} [pinchMinScale=0.3] Límite inferior del pinch (× escala base).
 * @property {number} [pinchMaxScale=3] Límite superior del pinch (× escala base).
 * @property {number} [idleRotationSpeed=0.002] Giro automático en Y (0 = casi quieto).
 * @property {{ x?: number, y?: number, z?: number }} [position] Offset respecto al origen del ancla (tras centrar por bbox).
 * @property {{ x?: number, y?: number, z?: number }} [rotation] Rotación inicial en radianes (orden XYZ de Three.js).
 */

/** @type {ARModelConfig[]} */
export const AR_MODEL_CONFIG = [
  {
    id: "vigilante_volcanes",
    path: "/models/volcanic_landscape_v2.glb",
    scale: 0.5,
    position: { x: 0,y: 0.7, z: 0 },
    rotation: { x: 1.5708, y: 0, z: 0 },
    animation: {
      enabled: true,
      timeScale: 1,
      loop: true,
    },
    pin: {
      show: true,
      text: "Modelo de demostración.",
    },
    pinchMinScale: 0.3,
    pinchMaxScale: 3,
    idleRotationSpeed: 0.002,
  },
  {
    id: "vigilante_volcanes",
    path: "/models/volcanic_landscape_v2.glb",
    scale: 0.5,
    position: { x: 0,y: 0.7, z: 0 },
    rotation: { x: 1.5708, y: 0, z: 0 },
    animation: {
      enabled: true,
      timeScale: 1,
      loop: true,
    },
    pin: {
      show: true,
      text: "Modelo de demostración.",
    },
    pinchMinScale: 0.3,
    pinchMaxScale: 3,
    idleRotationSpeed: 0.002,
  },


  {
    id: "cortado",
    path: "/models/Volcan3.glb",
    scale: 0.4,
    position: { x: 0, y:0.8, z: 0 },
    rotation: { x: 1.5708, y: 0, z: 0 },
    animation: {
      enabled: true,
      timeScale: 1,
      loop: true,
    },
    pin: {
      show: true,
      text: "Modelo de demostración.",
    },
    pinchMinScale: 0.3,
    pinchMaxScale: 3,
    idleRotationSpeed: 0.002,
  },
  {
    id: "Pag22",
    path: "/models/F2.glb",
    scale: 0.1, 
    position: { x: 0,y: 2, z: 0 },
    rotation: { x: 1.57, y: 0, z: 0 },
    animation: {
      enabled: true,
      clipNames: ["Full Activity"],
      timeScale: 1,
      loop: true,
    },
    pin: {
      show: true,
      text: "Este volcán tiene 3.000 m de altura",
    },
    pinchMinScale: 0.3,
    pinchMaxScale: 3,
    idleRotationSpeed: 0.002,
  },
  {
    id: "mapa_chileOK",
    path: "/models/chile_map.glb",
    scale: 0.2,
    animation: {
      enabled: true,
      timeScale: 1,
      loop: true,
    },
    pin: {
      show: true,
      text: "Este volcán tiene 3.000 m de altura",
    },
    pinchMinScale: 0.3,
    pinchMaxScale: 3,
    idleRotationSpeed: 0.002,
  },
  {
    id: "medicionPag39",
    path: "/models/Final4_elementos.glb",
    scale: 1,
    /** Traslación en el espacio del marcador (después de centrar el GLB por su caja). */
    position: { x: 0, y: 0.35, z: 0 },
    /** Rotación inicial en radianes (x, y, z). Ej.: y: Math.PI es media vuelta en Y. */
     rotation: { x: 1.5708, y: 0, z: 0 },
    animation: {
      enabled: true,
      timeScale: 1,
      loop: true,
    },
    pin: {
      show: true,
      text: "Lavamanos — información del objeto.",
    },
    pinchMinScale: 1,
    pinchMaxScale: 3,
    idleRotationSpeed: 0.002,
  },
  {
    id: "escuelaPag49",
    path: "/models/BF1.glb",
    scale: 0.6, 
    position: { x: 0,y: 0.4, z: 0 },
    rotation: { x: 1.5708, y: 0, z: 0 },
    animation: {
      enabled: true,
      timeScale: 1,
      loop: true,
    },
    pin: {
      show: true,
      text: "Este volcán tiene 3.000 m de altura",
    },
    pinchMinScale: 0.3,
    pinchMaxScale: 3,
    idleRotationSpeed: 0.002,
  },

];

const NUM_MODELS = AR_MODEL_CONFIG.length;
const VOLCAN3_MODEL_PATH = "/models/Volcan3.glb";
const VOLCAN3_MODEL_INDEX = AR_MODEL_CONFIG.findIndex(
  (c) => c.path === VOLCAN3_MODEL_PATH
);

/**
 * @param {object} elements
 * @param {HTMLElement} elements.container
 * @param {HTMLElement} elements.footer
 * @param {HTMLElement} elements.pinOverlay
 * @param {HTMLElement} elements.pinMessage
 * @param {HTMLElement} elements.reloadBtn
 * @returns {{ start: () => Promise<void>, stop: () => void, dispose: () => void }}
 */
export function createMindARVolcanSession(elements, navigate) {
  const { container, footer, pinOverlay, pinMessage, reloadBtn } = elements;

  let disposed = false;

  // MindAR global: un solo archivo .mind con varias imágenes (compiladas en orden fijo).
  const mindarThree = new MindARThree({
    container,
    imageTargetSrc: "/targets_volcan7.mind",
    maxTrack: 1,
    filterMinCF: 0.001,
    filterBeta: 0.05,
    warmupTolerance: 5,
    missTolerance: 12,
  });

  const { renderer, scene, camera } = mindarThree;

  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = false;

  const rendererBaseline = {
    outputEncoding: renderer.outputEncoding,
    toneMapping: renderer.toneMapping,
    toneMappingExposure: renderer.toneMappingExposure,
    useLegacyLights: renderer.useLegacyLights,
  };
  let volcan3RendererLookActive = false;
  function setVolcan3RendererLook(enabled) {
    if (VOLCAN3_MODEL_INDEX < 0) return;
    if (enabled === volcan3RendererLookActive) return;
    volcan3RendererLookActive = enabled;
    if (enabled) {
      // “Tal cual” la intención del `public/models/main.js` (adaptado a este renderer)
      renderer.outputEncoding = THREE.sRGBEncoding;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.7;
      renderer.useLegacyLights = false;
    } else {
      renderer.outputEncoding = rendererBaseline.outputEncoding;
      renderer.toneMapping = rendererBaseline.toneMapping;
      renderer.toneMappingExposure = rendererBaseline.toneMappingExposure;
      renderer.useLegacyLights = rendererBaseline.useLegacyLights;
    }
  }

  const light = new THREE.HemisphereLight(0xffffff, 0xbbbbff, 1.5);
  scene.add(light);

  const loader = new GLTFLoader();
  const clock = new THREE.Clock();

  const anchors = [];
  const models = [];
  const mixers = [];
  const actions = [];
  const modelEffects = Array(NUM_MODELS).fill(null);

  let activeModel = null;
  let activeModelIndex = -1;
  let activeMixer = null;
  let currentScale = 1;
  let targetRotationX = 0;
  let targetRotationY = 0;
  let isDragging = false;

  // Copia mutable de escalas (pinch persiste por modelo).
  const modelCurrentScales = AR_MODEL_CONFIG.map((c) => c.scale);
  /** Rotación X base por índice (alinear con targetRotationX al detectar el marcador). */
  const modelBaseRotationX = AR_MODEL_CONFIG.map(
    (c) => c.rotation?.x ?? 0
  );
  /** Rotación Y base por índice (para alinear con targetRotationY al detectar el marcador). */
  const modelBaseRotationY = AR_MODEL_CONFIG.map(
    (c) => c.rotation?.y ?? 0
  );
  const modelVisibleStates = Array(NUM_MODELS).fill(false);
  const lostTimeouts = Array(NUM_MODELS).fill(null);
  const LOST_GRACE_MS = 500;
  /** Evita múltiples navigate() si el target demo rebota en el tracking. */
  let demoRedirectDone = false;

  /** Texto por defecto del footer cuando no hay pin visible o mensaje cerrado. */
  const FOOTER_IDLE =
    "Escanea una tarjeta y desliza para rotar en horizontal y vertical";

  function closePinMessage() {
    pinMessage.classList.add("hide");
  }

  function togglePinMessage() {
    const texto = pinMessage.querySelector("p")?.innerText ?? "";
    if (pinMessage.classList.contains("hide")) {
      pinMessage.classList.remove("hide");
      footer.innerText = texto;
    } else {
      pinMessage.classList.add("hide");
      footer.innerText = FOOTER_IDLE;
    }
  }


  /**
   * Aplica el texto del pin según el modelo activo (solo si pin.show en config).
   */
  function applyPinForIndex(index) {
    const cfg = AR_MODEL_CONFIG[index];
    const p = pinMessage.querySelector("p");
    if (p && cfg?.pin?.text != null) {
      p.textContent = cfg.pin.text;
    }
  }

  for (let i = 0; i < NUM_MODELS; i++) {
    const anchor = mindarThree.addAnchor(i);
    anchors.push(anchor);

    anchor.onTargetFound = () => {
      if (lostTimeouts[i]) {
        clearTimeout(lostTimeouts[i]);
        lostTimeouts[i] = null;
      }

      const cfg = AR_MODEL_CONFIG[i];
      if (
        cfg.path === "/models/chile_map.glb" &&
        typeof navigate === "function" &&
        !demoRedirectDone
      ) {
        demoRedirectDone = true;
        navigate("/MapaChilePage");
        return;
      }
 

      activeModel = models[i];
      activeModelIndex = i;
      activeMixer = mixers[i] || null;
      currentScale = modelCurrentScales[i] ?? cfg.scale ?? 1;
      targetRotationX = clampPitchRad(i, modelBaseRotationX[i] ?? 0);
      targetRotationY = modelBaseRotationY[i] ?? 0;

      if (activeModel) {
        if (!modelVisibleStates[i]) {
          let scaleUp = 0;
          const animateIn = () => {
            if (!activeModel || activeModelIndex !== i) return;
            scaleUp += 0.05;
            if (scaleUp >= currentScale) scaleUp = currentScale;
            activeModel.scale.set(scaleUp, scaleUp, scaleUp);
            if (scaleUp < currentScale) requestAnimationFrame(animateIn);
          };
          animateIn();
        } else {
          activeModel.scale.set(currentScale, currentScale, currentScale);
        }
        modelVisibleStates[i] = true;
      }
      footer.innerText = "Modelo detectado";
    };

    anchor.onTargetLost = () => {
      if (lostTimeouts[i]) return;
      lostTimeouts[i] = setTimeout(() => {
        lostTimeouts[i] = null;
        if (activeModelIndex !== i) return;

        pinOverlay.classList.remove("show");
        closePinMessage();
        activeModel = null;
        activeModelIndex = -1;
        activeMixer = null;
        modelVisibleStates[i] = false;
        footer.innerText = "Escaneando...";
      }, LOST_GRACE_MS);
    };
  }

  AR_MODEL_CONFIG.forEach((cfg, index) => {
    loader.load(
      cfg.path,
      (gltf) => {
        const model = gltf.scene;

        if (!anchors[index]) {
          console.error(`Anchor ${index} no existe`);
          return;
        }

        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        model.position.sub(center);

        const p = cfg.position;
        if (p) {
          model.position.x += p.x ?? 0;
          model.position.y += p.y ?? 0;
          model.position.z += p.z ?? 0;
        }

        const s = cfg.scale;
        model.scale.set(s, s, s);
        modelCurrentScales[index] = s;

        const r = cfg.rotation;
        if (r) {
          model.rotation.x = r.x ?? 0;
          model.rotation.y = r.y ?? 0;
          model.rotation.z = r.z ?? 0;
        }

        if (cfg.path === VOLCAN3_MODEL_PATH) {
          // Luces como `public/models/main.js` (pero dentro del graph del modelo)
          const ambient = new THREE.AmbientLight(0xffffff, 1.2);
          const hemi = new THREE.HemisphereLight(0xb8d7ff, 0x3d2d1f, 1.8);
          hemi.position.set(0, 20, 0);

          const keyLight = new THREE.DirectionalLight(0xffffff, 4.2);
          keyLight.position.set(6, 8, 6);
          keyLight.castShadow = false;

          const fillLight = new THREE.DirectionalLight(0x9cc9ff, 2.2);
          fillLight.position.set(-8, 4, -4);
          fillLight.castShadow = false;

          const rimLight = new THREE.PointLight(0xffe0b5, 35, 120);
          rimLight.position.set(0, 6, -8);

          model.add(ambient);
          model.add(hemi);
          model.add(keyLight);
          model.add(fillLight);
          model.add(rimLight);

          model.traverse((obj) => {
            if (!obj || !obj.isMesh) return;
            obj.castShadow = false;
            obj.receiveShadow = false;
          });

          try {
            modelEffects[index] = createVolcan3Effects(model);
          } catch (e) {
            console.warn("Volcan3 efectos: error al inicializar:", e);
          }
        }

        anchors[index].group.add(model);
        models[index] = model;

        const animCfg = cfg.animation;
        if (
          animCfg?.enabled &&
          gltf.animations &&
          gltf.animations.length > 0
        ) {
          // console.log(
          //   `Modelo ${index} (${cfg.id}) animaciones:`,
          //   gltf.animations.map((a) => a.name)
          // );
          const mixer = new THREE.AnimationMixer(model);
          mixers[index] = mixer;
          const modelActions = {};
          gltf.animations.forEach((clip) => {
            modelActions[clip.name] = mixer.clipAction(clip);
          });
          actions[index] = modelActions;

          const explicitNames =
            animCfg.clipNames?.length > 0
              ? animCfg.clipNames
              : animCfg.clipName
                ? [animCfg.clipName]
                : null;
          let clipsToRun = gltf.animations;
          if (explicitNames) {
            clipsToRun = gltf.animations.filter((c) =>
              explicitNames.includes(c.name)
            );
            if (clipsToRun.length === 0) {
              console.warn(
                `Modelo ${index} (${cfg.id}): ningún clip coincide con [${explicitNames.join(", ")}]. En GLB:`,
                gltf.animations.map((a) => a.name)
              );
            }
          }

          const timeScale = animCfg.timeScale ?? 1;
          const loopEach = animCfg.loop !== false;
          clipsToRun.forEach((clip) => {
            const action = modelActions[clip.name];
            if (!action) return;
            action.reset();
            if (loopEach) {
              action.setLoop(THREE.LoopRepeat, Infinity);
            } else {
              action.setLoop(THREE.LoopOnce, 1);
              action.clampWhenFinished = true;
            }
            action.timeScale = timeScale;
            action.play();
          });
        } else if (animCfg && !animCfg.enabled) {
          console.log(`Modelo ${index} (${cfg.id}): animación desactivada en config.`);
        }
      },
      undefined,
      (error) => {
        console.error(`Error cargando modelo ${index} (${cfg.path}):`, error);
      }
    );
  });

  let previousTouch = null;
  let initialDistance = null;

  function getDistance(t1, t2) {
    return Math.sqrt(
      Math.pow(t1.clientX - t2.clientX, 2) +
        Math.pow(t1.clientY - t2.clientY, 2)
    );
  }

  function getActiveConfig() {
    if (activeModelIndex < 0) return null;
    return AR_MODEL_CONFIG[activeModelIndex];
  }

  /**
   * Límites de cabeceo (rotation.x) respecto a la pose inicial del modelo.
   * Evita inclinar tanto que la cámara “entre” por debajo y se vea la base.
   * Asimétrico: un poco más de margen hacia arriba / frente que hacia la vista inferior.
   */
  const PITCH_DELTA_BELOW_BASE = 0.52;
  const PITCH_DELTA_ABOVE_BASE = 0.42;

  function getPitchClampBounds(modelIndex) {
    const base = modelBaseRotationX[modelIndex] ?? 0;
    return {
      min: base - PITCH_DELTA_BELOW_BASE,
      max: base + PITCH_DELTA_ABOVE_BASE,
    };
  }

  function clampPitchRad(modelIndex, x) {
    if (modelIndex < 0) return x;
    const { min, max } = getPitchClampBounds(modelIndex);
    return Math.min(max, Math.max(min, x));
  }

  const onTouchStart = (event) => {
    if (!activeModel) return;
    if (event.touches.length === 1) {
      isDragging = true;
      previousTouch = event.touches[0];
    }
    if (event.touches.length === 2) {
      initialDistance = getDistance(event.touches[0], event.touches[1]);
    }
  };

  const onTouchMove = (event) => {
    if (!activeModel) return;
    if (isDragging && event.touches.length === 1) {
      const touch = event.touches[0];
      const deltaX = touch.clientX - previousTouch.clientX;
      const deltaY = touch.clientY - previousTouch.clientY;
      targetRotationY += deltaX * 0.01;
      targetRotationX += deltaY * 0.01;
      targetRotationX = clampPitchRad(activeModelIndex, targetRotationX);
      previousTouch = touch;
    }
    if (event.touches.length === 2) {
      const cfg = getActiveConfig();
      const minM = cfg?.pinchMinScale ?? 0.3;
      const maxM = cfg?.pinchMaxScale ?? 3;
      const distance = getDistance(event.touches[0], event.touches[1]);
      const scaleFactor = distance / initialDistance;
      let newScale = currentScale * scaleFactor;
      const baseScale = cfg?.scale ?? 1;
      newScale = Math.max(baseScale * minM, Math.min(newScale, baseScale * maxM));
      activeModel.scale.set(newScale, newScale, newScale);
      currentScale = newScale;
      const activeIndex = models.indexOf(activeModel);
      if (activeIndex !== -1) {
        modelCurrentScales[activeIndex] = newScale;
      }
      initialDistance = distance;
    }
  };

  const onTouchEnd = () => {
    isDragging = false;
    initialDistance = null;
  };

  container.addEventListener("touchstart", onTouchStart);
  container.addEventListener("touchmove", onTouchMove);
  container.addEventListener("touchend", onTouchEnd);

  const onReload = () => {
    footer.innerText = "Reiniciando cámara...";
    reloadBtn.disabled = true;
    setTimeout(() => window.location.reload(), 500);
  };

  reloadBtn.addEventListener("click", onReload);

  const onOrientation = () => {
    setTimeout(() => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }, 300);
  };

  window.addEventListener("orientationchange", onOrientation);

  let animationLoop = null;

  const start = async () => {
    if (!window.isSecureContext) {
      footer.classList.add("show");
      footer.innerText =
        "La cámara solo funciona en HTTPS. Abre el sitio con https://...";
      return;
    }
    footer.classList.add("show");
    footer.innerText = "Solicitando cámara...";
    try {
      await mindarThree.start();
      footer.innerText = FOOTER_IDLE;

      animationLoop = () => {
        const delta = clock.getDelta();
        const t = clock.elapsedTime;
        const volcan3Active =
          VOLCAN3_MODEL_INDEX >= 0 &&
          activeModelIndex === VOLCAN3_MODEL_INDEX &&
          activeModel != null;
        setVolcan3RendererLook(volcan3Active);
        if (activeMixer) {
          activeMixer.update(delta);
        }
        for (let i = 0; i < NUM_MODELS; i++) {
          const fx = modelEffects[i];
          if (!fx || !modelVisibleStates[i]) continue;
          fx.update(delta, t);
        }
        const cfg = getActiveConfig();
        const idleSpeed = cfg?.idleRotationSpeed ?? 0.002;
        if (activeModel && !isDragging) {
          activeModel.rotation.x +=
            (targetRotationX - activeModel.rotation.x) * 0.1;
          activeModel.rotation.x = clampPitchRad(
            activeModelIndex,
            activeModel.rotation.x
          );
          targetRotationX = clampPitchRad(activeModelIndex, targetRotationX);
          activeModel.rotation.y +=
            (targetRotationY - activeModel.rotation.y) * 0.1;
          activeModel.rotation.y += idleSpeed;
        }
        renderer.render(scene, camera);
      };
      renderer.setAnimationLoop(animationLoop);
    } catch (err) {
      const errorName =
        err?.name ||
        (typeof err === "string" && err.includes("NotReadableError")
          ? "NotReadableError"
          : "UnknownError");
      const errorMessage =
        err?.message || (typeof err === "string" ? err : "");

      console.error("Error al iniciar AR:", {
        name: errorName,
        message: errorMessage,
        raw: err,
      });

      let msg = "No se pudo abrir la cámara. ";
      if (errorName === "NotAllowedError") {
        msg += "Permiso denegado. Permite la cámara en el navegador.";
      } else if (
        errorName === "NotReadableError" ||
        (errorMessage && errorMessage.includes("Device in use"))
      ) {
        msg +=
          "La cámara está en uso por otra app o pestaña. Ciérralas y recarga.";
      } else if (
        (errorMessage && errorMessage.includes("targets")) ||
        (errorMessage && errorMessage.includes("404"))
      ) {
        msg += "Falta el archivo de marcadores (.mind) en el servidor.";
      } else {
        msg +=
          "Recarga la página e intenta de nuevo." +
          (errorMessage ? ` (${errorMessage})` : "");
      }
      footer.innerText = msg;
    }
  };

  const stop = () => {
    setVolcan3RendererLook(false);
    renderer.setAnimationLoop(null);
    animationLoop = null;
    mindarThree.stop();
  };

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    stop();
    try {
      container.querySelectorAll("video").forEach((v) => {
        const stream = v.srcObject;
        if (stream && typeof stream.getTracks === "function") {
          stream.getTracks().forEach((t) => t.stop());
        }
        v.srcObject = null;
      });
    } catch (e) {
      /* ignore */
    }
    lostTimeouts.forEach((id, idx) => {
      if (id) {
        clearTimeout(id);
        lostTimeouts[idx] = null;
      }
    });
    container.removeEventListener("touchstart", onTouchStart);
    container.removeEventListener("touchmove", onTouchMove);
    container.removeEventListener("touchend", onTouchEnd);
    reloadBtn.removeEventListener("click", onReload);
    window.removeEventListener("orientationchange", onOrientation);
 
  };

  return { start, stop, dispose };
}
